import { FC, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Modal, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import { useProcCreateSimple } from '@/shared/api';
import { useRouterPrivateContext } from '@/shared/router';
import { Col, Row } from '@/shared/ui';
import { getRandomAdjective } from '@/shared/utils';

type Props = {
  open: boolean;
  onCancel: () => void;
};

const { Title } = Typography;

export const splitStringIntoSteps = (text: string) => {
  const array = text
    .substring(2)
    .split('\n• ')
    .filter((item) => item !== '');

  const steps: { title: string; text: string }[] | null = [];

  for (const [index, item] of array.entries()) {
    let title = item.substring(0, item.indexOf(':')).trim();
    const text = item.substring(item.indexOf(':') + 1).trim();

    if (!title) {
      title = `Step ${index + 1}`;
    }

    steps.push({ title, text });
  }

  return steps;
};

export const SimpleCreateProcModal: FC<Props> = ({ open, onCancel }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { selectedWorkspace } = useRouterPrivateContext();

  const [procTitle, setProcTitle] = useState(
    `My ${getRandomAdjective()} new proc`,
  );
  const [stepsDefinitionValue, setStepsDefinitionValue] = useState('');

  const {
    mutate: createProcBySpecification,
    isLoading: createProcBySpecificationLoading,
  } = useProcCreateSimple();

  const formatStepsDefinitionValue = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;

    // If user removes space after bullet point, remove whole line
    if (value.charAt(value.length - 1) === '•') {
      const formattedValue = value.substring(0, value.lastIndexOf('\n'));
      setStepsDefinitionValue(formattedValue);
      return;
    }

    // Split the text into an array of lines
    const lines = value.split('\n');

    // Iterate over each line
    for (let i = 0; i < lines.length; i++) {
      // Check if the line doesn't start with "• "
      if (!lines[i].startsWith('• ')) {
        // Add "• " to the beginning of the line
        lines[i] = '• ' + lines[i];
      } else if (lines[i] === '•') {
        // Remove "• " if the line only contains it and backspace is pressed
        lines.splice(i, 1);
      }
    }

    // Join the modified lines into a single string with newline characters
    setStepsDefinitionValue(lines.join('\n'));
  };

  const handleCreateProcBySpecification = () => {
    const steps = splitStringIntoSteps(stepsDefinitionValue);
    if (!steps) {
      showNotification({
        type: 'error',
        message: 'Create proc error',
        description:
          'Please make sure that each step has a "title" and "text" separated by colon \':\'',
      });
      return;
    }
    createProcBySpecification(
      {
        body: {
          workspaceId: selectedWorkspace?.id!,
          title: procTitle,
          steps,
        },
      },
      {
        onSuccess(data: any) {
          queryClient.invalidateQueries([
            'dashboardProcsGetPaginated',
            'myProcs',
            selectedWorkspace?.id,
          ]);
          navigate(`/dashboard/author/proc/${data.id}`, {
            state: { procId: data.id },
          });
        },
        onError: (error: any) => {
          showNotification({
            type: 'error',
            message: 'Create proc error',
            description: error.stack.message,
          });
        },
      },
    );
  };

  return (
    <Modal
      data-testid="simple-create-proc-modal"
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={2} style={{ lineHeight: 1, marginTop: 15 }}>
            Create new proc
          </Title>
        </Col>
      }
      destroyOnClose={true}
      width={700}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <>
        <div style={{ marginBottom: 30 }}>
          Use form below to specify your proc.
        </div>
        <Form layout="vertical">
          <Form.Item label="Title">
            <Input
              value={procTitle}
              onChange={(e) => setProcTitle(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Steps definition" style={{ width: '100%' }}>
            <Input.TextArea
              rows={6}
              placeholder={
                'Example: \n• Step 1: Do something \n• Step 2: Do something else\n...\nEverything before ":" will be step title, while everything after ":" will be text component on that step.'
              }
              value={stepsDefinitionValue}
              onChange={formatStepsDefinitionValue}
            />
          </Form.Item>
        </Form>

        <Actions>
          <ButtonsWrapper>
            <Button
              onClick={onCancel}
              style={{ width: '40%', height: 40, marginTop: 30 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={createProcBySpecificationLoading}
              disabled={
                !procTitle ||
                !stepsDefinitionValue ||
                stepsDefinitionValue === '• '
              }
              onClick={handleCreateProcBySpecification}
              style={{ width: '60%', height: 40, marginTop: 30 }}
            >
              Create a proc
            </Button>
          </ButtonsWrapper>
        </Actions>
      </>
    </Modal>
  );
};

const Actions = styled(Row)`
  justify-content: flex-end;
  margin-top: 30px;
  width: 100%;
`;

const ButtonsWrapper = styled(Row)`
  justify-content: space-between;
  width: 100%;
  max-width: 450px;
`;
